import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { formatErr, STATUS_LABELS, statusBadgeClass, fmtMin, fmtLessonWhen } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { OutcomeBadge, OverallSummary, fmtWhen } from "./AssessmentsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Loader2, AlertCircle, Send, FileDown } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [sentCode, setSentCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [anomaly, setAnomaly] = useState("");
  const [anomalyLesson, setAnomalyLesson] = useState("");
  const [anomalyOpen, setAnomalyOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/${token}/info`)
      .then((r) => {
        setInfo(r.data);
        if (!r.data.otp_required) loadRegister();
      })
      .catch((e) => setError(formatErr(e.response?.data?.detail)));
    // eslint-disable-next-line
  }, [token]);

  const loadRegister = async () => {
    try { const r = await axios.get(`${API}/public/${token}`); setData(r.data); }
    catch (e) { /* otp required */ }
  };

  const requestOtp = async () => {
    setLoading(true); setError("");
    try { const r = await axios.post(`${API}/public/${token}/request-otp`); setSentCode(r.data.simulated_code || "sent"); toast.success(r.data.message); }
    catch (e) { setError(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const verify = async () => {
    setLoading(true); setError("");
    try { const r = await axios.post(`${API}/public/${token}/verify-otp`, { code }); setData(r.data); }
    catch (e) { setError(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/public/${token}/pdf`, { params: { key: data.pdf_key }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `registro_personale_${data.participant.last_name}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Sessione scaduta: ricarica la pagina e ripeti la verifica per scaricare il PDF."); }
  };
  const sendAnomaly = async () => {
    try { await axios.post(`${API}/public/${token}/report-anomaly`, { message: anomaly, lesson_id: anomalyLesson || null }); toast.success("Segnalazione inviata all'amministrazione"); setAnomaly(""); setAnomalyLesson(""); setAnomalyOpen(false); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  if (error && !info) return <Centered><ErrorCard msg={error} /></Centered>;
  if (!info) return <Centered><Loader2 className="animate-spin text-slate-400" /></Centered>;

  // OTP gate
  if (!data) {
    return (
      <Centered>
        <Card className="w-full max-w-md p-8 border-slate-200" data-testid="public-participant-portal">
          <div className="flex items-center gap-2 mb-6 text-[#0B192C]"><img src="/logo_csa.jpg" alt="Centro Studi Athena" className="h-8 w-8 object-contain" data-testid="portal-otp-logo" /><span className="font-display font-bold">Centro Studi Athena</span></div>
          <div className="h-12 w-12 rounded-xl bg-[#008C45]/10 text-[#008C45] grid place-items-center mb-4"><ShieldCheck /></div>
          <h1 className="text-xl font-display font-bold text-slate-900">Verifica di sicurezza</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Per consultare il tuo registro presenze ({info.participant_initials}) richiedi un codice di verifica alla tua e-mail.</p>
          {error && <div className="mb-4 text-sm text-rose-600">{error}</div>}
          {!sentCode ? (
            <Button onClick={requestOtp} disabled={loading} data-testid="request-otp-btn" className="w-full bg-[#0B192C] hover:bg-[#07111E]">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Invia codice via e-mail
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-xs px-3 py-2" data-testid="otp-sent-info">
                {sentCode === "sent" ? "Abbiamo inviato il codice al suo indirizzo e-mail. Controlli anche la cartella spam." : <>Modalità simulazione — codice: <strong className="font-mono">{sentCode}</strong></>}
              </div>
              <div><Label>Codice di verifica</Label><Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="mt-1 font-mono text-center text-lg tracking-widest" data-testid="otp-input" /></div>
              <Button onClick={verify} disabled={loading} data-testid="verify-otp-btn" className="w-full bg-[#008C45] hover:bg-[#006B34]">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accedi al registro
              </Button>
            </div>
          )}
        </Card>
      </Centered>
    );
  }

  // Registro view
  const s = data.summary;
  const chartData = data.lessons.map((l) => ({ name: `L${l.number}`, assenza: Math.round(l.cumulative_absence_min / 60 * 100) / 100 }));
  const maxAbsHours = s.max_absence_hours;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0B192C] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2"><img src="/logo_csa.jpg" alt="Centro Studi Athena" className="h-8 w-8 rounded-md bg-white object-contain p-0.5" data-testid="portal-logo" /><span className="font-display font-bold">Centro Studi Athena</span></div>
          <span className="text-xs text-slate-300">Registro personale</span>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="public-register">
        <h1 className="text-2xl font-display font-extrabold text-slate-900">{data.participant.first_name} {data.participant.last_name}</h1>
        <p className="text-sm text-slate-500">{data.course.title} · {data.course.code}</p>
        <button type="button" onClick={downloadPdf} data-testid="public-download-pdf"
          className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-[#0B192C] text-white text-sm font-medium hover:bg-[#07111E] transition-colors">
          <FileDown size={15} /> Scarica registro personale (PDF)
        </button>

        {data.note_visible && (
          <div className="mt-4 rounded-lg bg-sky-50 border border-sky-200 text-sky-800 text-sm px-4 py-3">{data.note_visible}</div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {[
            { l: "Ore frequentate", v: `${s.total_recognized_hm} su ${s.total_programmed_hm}`, c: "text-emerald-700" },
            { l: "Assenza (% monte ore)", v: `${s.absence_pct_progressive}%` },
            { l: "Ore di assenza", v: s.total_absence_hm, c: "text-rose-700" },
            { l: "Ore assenza residue", v: s.residual_absence_hm },
          ].map((k) => (
            <Card key={k.l} className="p-4 border-slate-200">
              <div className="text-xs text-slate-500 uppercase tracking-wider">{k.l}</div>
              <div className={`text-2xl font-display font-extrabold tabular mt-1 ${k.c || "text-slate-900"}`}>{k.v}</div>
            </Card>
          ))}
        </div>

        <Card className="p-5 border-slate-200 mt-6">
          <h3 className="font-display font-bold text-slate-900 mb-1">Andamento assenze</h3>
          <p className="text-xs text-slate-500 mb-4">Soglia massima consentita: {s.max_absence_hm} su {s.course_total_hm} totali.</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData} margin={{ left: -20 }}>
              <defs><linearGradient id="gp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#DC2626" stopOpacity={0.3} /><stop offset="95%" stopColor="#DC2626" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={11} stroke="#94a3b8" /><YAxis fontSize={11} stroke="#94a3b8" unit="h" />
              <Tooltip /><ReferenceLine y={maxAbsHours} stroke="#DC2626" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="assenza" stroke="#DC2626" fill="url(#gp)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {data.assessments?.length > 0 && (
          <Card className="border-slate-200 mt-6 overflow-hidden" data-testid="public-assessments">
            <div className="px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-700">Verifiche</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase"><tr>
                  <th className="text-left px-4 py-2">Test</th><th className="text-left px-4 py-2">Data e ora</th><th className="text-left px-4 py-2">Punteggio</th>
                  <th className="text-left px-4 py-2">%</th><th className="text-left px-4 py-2">Esito</th>
                </tr></thead>
                <tbody>
                  {data.assessments.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100" data-testid={`public-assessment-${a.id}`}>
                      <td className="px-4 py-2 font-medium">{a.name}</td>
                      <td className="px-4 py-2 text-xs text-slate-500" data-testid={`public-assessment-when-${a.id}`}>{fmtWhen(a.result)}</td>
                      <td className="px-4 py-2 tabular">{a.result && a.result.score !== null ? `${String(a.result.score).replace(".", ",")} / ${a.max_score}` : "—"}</td>
                      <td className="px-4 py-2 tabular">{a.result && a.result.pct !== null ? `${a.result.pct}%` : "—"}</td>
                      <td className="px-4 py-2"><OutcomeBadge r={a.result} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <OverallSummary s={data.assessment_summary} />
            <p className="px-4 py-2 text-xs text-slate-400">L'esito complessivo si basa sulla media percentuale di tutti i test (soglia {data.assessment_summary?.pass_pct}%).</p>
          </Card>
        )}

        <Card className="border-slate-200 mt-6 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-700">Dettaglio lezioni</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase"><tr>
                <th className="text-left px-4 py-2">Lez.</th><th className="text-left px-4 py-2">Data e orario</th>
                <th className="text-left px-4 py-2">Presenza</th><th className="text-left px-4 py-2">Assenza</th>
                <th className="text-left px-4 py-2">Accessi</th><th className="text-left px-4 py-2">Stato</th>
              </tr></thead>
              <tbody>
                {data.lessons.map((l) => (
                  <tr key={l.number} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-mono">{l.number}</td>
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap" data-testid={`portal-lesson-when-${l.number}`}>{fmtLessonWhen(l)}</td>
                    <td className="px-4 py-2 text-emerald-700 tabular">{fmtMin(l.presence_min)}</td>
                    <td className="px-4 py-2 text-rose-700 tabular">{fmtMin(l.absence_min)}</td>
                    <td className="px-4 py-2">{l.num_accessi}</td>
                    <td className="px-4 py-2"><span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(l.status)}`}>{STATUS_LABELS[l.status] || l.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="mt-6">
          <Dialog open={anomalyOpen} onOpenChange={setAnomalyOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="report-anomaly-btn"><AlertCircle size={15} className="mr-1" /> Segnala un'anomalia</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Segnala un'anomalia</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">La segnalazione sarà inviata all'amministrazione e non modificherà automaticamente i dati.</p>
              <Label>Lezione interessata</Label>
              <select value={anomalyLesson} onChange={(e) => setAnomalyLesson(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="anomaly-lesson-select">
                <option value="">Generale (nessuna lezione specifica)</option>
                {data.lessons.map((l) => <option key={l.id} value={l.id}>Lezione {l.number}{l.title ? ` – ${l.title}` : ""}{l.date ? ` (${new Date(l.date).toLocaleDateString("it-IT")})` : ""}</option>)}
              </select>
              <Textarea rows={4} value={anomaly} onChange={(e) => setAnomaly(e.target.value)} placeholder="Descrivi l'anomalia riscontrata…" data-testid="anomaly-input" />
              <DialogFooter><Button onClick={sendAnomaly} disabled={!anomaly.trim()} data-testid="anomaly-submit-btn" className="bg-[#0B192C] hover:bg-[#07111E]"><Send size={14} className="mr-1" /> Invia</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <p className="text-xs text-slate-400 mt-8 text-center">Per segnalazioni contatta l'amministrazione del Centro Studi Athena.</p>
      </main>
    </div>
  );
}

const Centered = ({ children }) => <div className="min-h-screen grid place-items-center bg-slate-50 p-4">{children}</div>;
const ErrorCard = ({ msg }) => (
  <Card className="w-full max-w-md p-8 border-slate-200 text-center">
    <AlertCircle className="mx-auto text-rose-500 mb-3" />
    <h1 className="font-display font-bold text-slate-900">Link non accessibile</h1>
    <p className="text-sm text-slate-500 mt-1">{msg}</p>
  </Card>
);
